import { Constrained, EnumKey, EnumValue, keysOfNumericEnum } from '../common/tsUtils'

import { WbAccountModel } from '../server/mpsklad_core/Models/WbAccountModel'
import { IntegrationType } from '../server/mpsklad_core/Entity/IntegrationType'
import { OzonAccountModel } from '../server/mpsklad_core/Models/OzonAccountModel'
import { AccountModelBase } from '../server/mpsklad_core/Models/AccountModelBase'
import { MoySkladAccountModel } from '../server/mpsklad_core/Models/MoySkladAccountModel'
import { YandexMarketAccountModel } from '../server/mpsklad_core/Models/YandexMarketAccountModel'

export type StoreIntegrationType =
  Exclude<EnumValue<typeof IntegrationType>, IntegrationType.MoySklad>

// TODO: Replace with IntegrationType where possible
export type IntegrationTypeKey = EnumKey<typeof IntegrationType>

export type StoreIntegrationTypeKey = Exclude<IntegrationTypeKey, 'MoySklad'>

export type IntegrationAccountTypeMap =
  Constrained<{ [I in IntegrationTypeKey]: AccountModelBase }, {
    Ozon: OzonAccountModel,
    Wildberries: WbAccountModel,
    MoySklad: MoySkladAccountModel,
    YandexMarket: YandexMarketAccountModel
  }>

export type SyncAccountInfo<I extends IntegrationTypeKey> = {
  type: I

  account: IntegrationAccountTypeMap[I]

  displayName: string
}

export type AllowedIntegrationsMap = {
  readonly [integrationType in IntegrationType]: boolean
}

export const integrationNames: {
  readonly [integration in IntegrationTypeKey]: string
} = {
  Ozon: 'Ozon',
  Wildberries: 'Wildberries',
  MoySklad: 'МойСклад',
  YandexMarket: 'Yandex.Market'
}

export const integrationSlugs = {
  Ozon: 'Ozon',
  Wildberries: 'Wb',
  MoySklad: 'Ms',
  YandexMarket: 'Ym'
} as const

export const integrationColors: {
  readonly [integration in IntegrationTypeKey]: string
} = {
  Ozon: '#3987cf',
  Wildberries: '#a11f94',
  MoySklad: '#2855af',
  YandexMarket: '#FFDD00'
}

export const integrationTypeNames: {
  readonly [integrationType in IntegrationType]: string
} = {
  [IntegrationType.Ozon]: integrationNames.Ozon,
  [IntegrationType.Wildberries]: integrationNames.Wildberries,
  [IntegrationType.MoySklad]: integrationNames.MoySklad,
  [IntegrationType.YandexMarket]: integrationNames.YandexMarket
}

export const integrationTypeSlugs = {
  [IntegrationType.Ozon]: integrationSlugs.Ozon,
  [IntegrationType.Wildberries]: integrationSlugs.Wildberries,
  [IntegrationType.YandexMarket]: integrationSlugs.YandexMarket,
  [IntegrationType.MoySklad]: integrationSlugs.MoySklad
} as const

export const integrationTypeByKey: {
  readonly [integration in IntegrationTypeKey]: IntegrationType
} = {
  Ozon: IntegrationType.Ozon,
  Wildberries: IntegrationType.Wildberries,
  MoySklad: IntegrationType.MoySklad,
  YandexMarket: IntegrationType.YandexMarket
}

export const storeIntegrationKeyByType: {
  readonly [integration in StoreIntegrationType]: StoreIntegrationTypeKey
} = {
  [IntegrationType.Ozon]: 'Ozon',
  [IntegrationType.Wildberries]: 'Wildberries',
  [IntegrationType.YandexMarket]: 'YandexMarket'
}

export const integrationKeyByType: {
  readonly [integration in IntegrationType]: IntegrationTypeKey
} = {
  [IntegrationType.Ozon]: 'Ozon',
  [IntegrationType.Wildberries]: 'Wildberries',
  [IntegrationType.MoySklad]: 'MoySklad',
  [IntegrationType.YandexMarket]: 'YandexMarket'
}

export const allIntegrationTypeKeys = keysOfNumericEnum(IntegrationType)

export const isIntegrationTypeKey =
  (integration: string | null | undefined): integration is IntegrationTypeKey => {
    if (integration == null) {
      return false
    }

    const allIntegrationTypeStrings: string[] = allIntegrationTypeKeys satisfies string[]

    return allIntegrationTypeStrings.includes(integration)
  }

/** @deprecated Use isIntegrationTypeKey */
export const isIntegrationType = isIntegrationTypeKey